// Classic /schedule: stacked division standings (#583, DECISIONS 4, UX §6).
// "All divisions" is one table per division, in admin order, never one merged
// table; one division is just its own table. Each table has its own <h2> and a
// <caption>, in the card look of Home's standings (SwipeableStandings).
//
// Mounted only when the division switcher is visible, so a league without
// divisions never calls useDivisionStandings (no request, no DOM).

import { useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTeamDisplay } from '@/utils/teamDisplay'
import { useDivisionStandings } from '../../../core/data/useDivisionStandings'
import { StandingsTable } from './SwipeableStandings'
import { StandingsSkeleton } from './Skeleton'
import { seasonWithoutDivision } from './DivisionPills'
import { asFeatured, seasonLabel } from './divisionStandingsModel'
import { useLabels } from '../../../core/labels'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'

/** Under "All divisions" each table shows its top rows and links to the full one. */
export const STACKED_STANDINGS_ROWS = 8

const card = 'bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4'

// `stacked`: "All divisions" (top rows per division, each linking to its full
// table); otherwise the one division's full table.
export default function DivisionStandings({ stacked }: { stacked: boolean }) {
  const { status, tables, retry } = useDivisionStandings(true)
  const navigate = useNavigate()
  const teamName = useTeamDisplay()
  const t = useLabels()
  const m = useT(siteClassicMessages).schedule.standings
  const core = useT(siteCoreMessages).standings
  // The first load renders nothing until the tables are in: a skeleton here,
  // under content that is still growing, gets pushed down the screen (a layout
  // shift on phones). Later loads (another division picked) keep the skeleton.
  const loadedOnce = useRef(false)
  if (status === 'ready') loadedOnce.current = true

  if (status === 'idle') return null
  if (status === 'loading' && tables.length === 0 && !loadedOnce.current) return null
  return (
    <section className="mt-10" aria-label={t.term('standings')}>
      {status === 'loading' && tables.length === 0 ? (
        <div className={card}>
          <div className="h-5 w-40 bg-white/10 animate-pulse rounded mb-3" />
          <StandingsSkeleton />
        </div>
      ) : status === 'error' && tables.length === 0 ? (
        <div className={`${card} text-center`}>
          <p className="text-white/60 text-sm mb-3">{m.loadError}</p>
          <button
            type="button"
            onClick={retry}
            className="px-4 py-2 min-h-[44px] bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-lg font-bold text-sm transition-colors"
          >
            {t.ui.common.retry}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {tables.map((table) => {
            const season = seasonLabel(table.season)
            const clipped = stacked && table.rows.length > STACKED_STANDINGS_ROWS
            return (
              <div key={table.division.id} className={card}>
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h2 className="text-base sm:text-lg font-['Montserrat'] font-black text-white uppercase tracking-tight">
                    {m.heading(table.division.name)}
                  </h2>
                  {season && (
                    <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold text-right">{seasonWithoutDivision(season, table.division.name)}</span>
                  )}
                </div>
                {table.rows.length > 0 ? (
                  <StandingsTable
                    featured={asFeatured(table, clipped ? STACKED_STANDINGS_ROWS : null)}
                    isBracketTournament={false}
                    onTeamClick={(teamId) => navigate(`/teams/${teamId}`)}
                    teamName={teamName}
                    caption={m.caption(table.division.name, season)}
                    fullHeight
                  />
                ) : (
                  <div className="bg-bg-primary/12 border border-secondary/30 p-4 text-center text-white/60 text-sm">
                    {core.noData}
                  </div>
                )}
                {clipped && (
                  <div className="mt-3 text-right">
                    <Link
                      to={{ search: `?division=${table.division.slug}` }}
                      className="inline-flex items-center min-h-[44px] text-[11px] uppercase tracking-[0.22em] font-bold text-primary hover:underline underline-offset-4"
                    >
                      {core.fullTable} ›<span className="sr-only">: {table.division.name}</span>
                    </Link>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
